import { FC } from 'react';
import { DisplayMetric } from '../../../../../gql/graphql';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

type Props = Readonly<{
	metric: Pick<DisplayMetric, 'values' | 'expectations' | 'trendComparison' | 'precision'>;
}>;

type TrendComparison = DisplayMetric['trendComparison'];

const formatComparison = (trendComparison: TrendComparison, comparison: number, precision: number = 0): string => {
	if (trendComparison === 'relative') {
		const sign = comparison < 1 ? '−' : '+';

		const formattedValue = new Intl.NumberFormat(undefined, {
			maximumFractionDigits: Math.max(0, Math.min(20, precision)), // clamp precision between 0 and 20
		}).format(Math.round(Math.abs((comparison - 1) * 100)));

		return `${sign}${formattedValue}%`;
	}

	const sign = comparison < 0 ? '−' : '+';

	const formattedValue = new Intl.NumberFormat(undefined, {
		maximumFractionDigits: Math.max(0, Math.min(20, precision)), // clamp precision between 0 and 20
	}).format(Math.abs(comparison));

	return `${sign}${formattedValue}`;
};

const MetricValueComparison: FC<Props> = ({ metric }) => {
	const theme = useTheme();

	if (metric.values.comparison == null || metric.expectations == null) {
		return null;
	}

	const isUp = metric.values.comparison >= 1;
	const Icon = isUp ? ArrowDropUpIcon : ArrowDropDownIcon;

	const isPositive = (isUp && metric.expectations === 'increase') || (!isUp && metric.expectations === 'decrease');
	const color = isPositive ? theme.palette.success.main : theme.palette.error.main;

	return (
		<>
			<Icon style={{ color }} />
			<Typography>{formatComparison(metric.trendComparison, metric.values.comparison, metric.precision ?? undefined)}</Typography>
		</>
	);
};

export { MetricValueComparison, formatComparison };
